import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, ClipboardList, Download, History, Loader2, Rocket, Send, Sparkles, Users, XCircle } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { bizDate, bizDateTime } from '@/lib/dates';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const TB_FOLLOWUP_STATES = [
  ['pendiente_contacto', 'Pendiente de contacto', 'Pending contact'],
  ['propuesta_enviada', 'Propuesta formal enviada', 'Formal proposal sent'],
  ['en_coordinacion', 'En coordinación', 'In coordination'],
  ['contratado', 'Contratado', 'Contracted'],
  ['no_continuara', 'No continuará', 'Will not continue'],
];
const TB_STATE_LABEL = (s, isES) => {
  const row = TB_FOLLOWUP_STATES.find((x) => x[0] === s);
  return row ? (isES ? row[1] : row[2]) : s;
};

// Wave B — HM workspace: client campaign requests queue + Team Building manager.
// `section` limits rendering: 'requests' | 'teambuilding' | undefined (both).
export default function AdminClimaraWaveB({ companyId, section }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const locale = isES ? 'es' : 'en';

  const [requests, setRequests] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [qSel, setQSel] = useState({});
  const [reports, setReports] = useState([]);
  const [reportSel, setReportSel] = useState('');
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // FIX 22 — lightweight follow-up form state
  const [fuOpen, setFuOpen] = useState(null);
  const [fu, setFu] = useState({ action_text: '', follow_up_date: '', state: 'pendiente_contacto', note: '' });
  const [histOpen, setHistOpen] = useState(null);

  // V5.1 §E — executive Team Building PDF (any status, internal view)
  const downloadTbPdf = async (pid) => {
    try {
      const r = await api.get(`/admin/climara/team-building/${pid}/pdf?locale=${isES ? 'es' : 'en'}`,
        { responseType: 'blob', timeout: 90000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `team-building-${pid.slice(0, 8)}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const registerFollowUp = async (pid) => {
    if (fu.action_text.trim().length < 3) {
      toast.error(isES ? 'Describe la acción realizada o el próximo paso.' : 'Describe the action taken or next step.');
      return;
    }
    setBusy(`fu-${pid}`);
    try {
      await api.post(`/admin/climara/team-building/${pid}/follow-up`, {
        action_text: fu.action_text.trim(),
        follow_up_date: fu.follow_up_date || null,
        state: fu.state,
        note: fu.note.trim() || null,
      });
      toast.success(isES ? 'Seguimiento registrado' : 'Follow-up registered');
      setFuOpen(null);
      setFu({ action_text: '', follow_up_date: '', state: 'pendiente_contacto', note: '' });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const load = useCallback(async () => {
    const [reqs, tb, reps, qs] = await Promise.all([
      adminApi.climaraCampaignRequests().catch(() => ({ items: [] })),
      adminApi.climaraTeamBuildingList(companyId || null).catch(() => ({ items: [] })),
      companyId ? adminApi.climaraListReports(companyId).catch(() => []) : Promise.resolve([]),
      api.get('/admin/questionnaires', { params: { suite: 'climara', status: 'published' } })
        .then((r) => r.data).catch(() => []),
    ]);
    setRequests((reqs?.items || []).filter((r) => r.status === 'pending'));
    setProposals(tb?.items || []);
    const repList = Array.isArray(reps) ? reps : (reps?.items || []);
    setReports(repList);
    const qList = Array.isArray(qs) ? qs : (qs?.items || []);
    setQuestionnaires(qList.filter((q) => (q.suite_codes || []).includes('climara') && q.status === 'published'));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const approve = async (rid) => {
    const qid = qSel[rid];
    if (!qid) { toast.error(isES ? 'Selecciona el cuestionario certificado.' : 'Pick the certified questionnaire.'); return; }
    setBusy(rid);
    try {
      const res = await adminApi.climaraApproveCampaignRequest(rid, qid, null);
      toast.success(isES
        ? `Campaña lanzada — ${res.assignments} invitaciones enviadas`
        : `Campaign launched — ${res.assignments} invitations sent`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const reject = async (rid) => {
    if (rejectReason.trim().length < 5) { toast.error(isES ? 'Motivo de 5+ caracteres.' : '5+ char reason.'); return; }
    setBusy(rid);
    try {
      await adminApi.climaraRejectCampaignRequest(rid, rejectReason.trim());
      toast.success(isES ? 'Solicitud rechazada' : 'Request rejected');
      setRejecting(null); setRejectReason('');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const generate = async () => {
    if (!reportSel) { toast.error(isES ? 'Selecciona un informe publicado.' : 'Pick a released report.'); return; }
    setBusy('gen');
    try {
      await adminApi.climaraGenerateTeamBuilding(reportSel, locale);
      toast.success(isES ? 'Recomendaciones generadas (borrador)' : 'Recommendations generated (draft)');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const propose = async (pid) => {
    setBusy(pid);
    try {
      await adminApi.climaraProposeTeamBuilding(pid);
      toast.success(isES ? 'Propuesta enviada al cliente' : 'Proposal sent to client');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const statusBadge = (s) => {
    const map = {
      draft: ['bg-muted text-muted-foreground', isES ? 'Borrador' : 'Draft'],
      proposed: ['bg-amber-100 text-amber-700', isES ? 'Propuesta al cliente' : 'Proposed to client'],
      accepted: ['bg-emerald-100 text-emerald-700', isES ? 'Aceptada' : 'Accepted'],
      declined: ['bg-rose-100 text-rose-700', isES ? 'Declinada' : 'Declined'],
    };
    const [cls, label] = map[s] || map.draft;
    return <Badge className={`text-xs ${cls}`}>{label}</Badge>;
  };

  return (
    <>
      {/* Campaign requests queue */}
      {section !== 'teambuilding' && (
      <Card className="p-6 shadow-card" data-testid="climara-requests-section">
        <div className="flex items-center gap-2 mb-3">
          <Rocket className="h-5 w-5 text-primary" />
          <h3 className="font-display font-bold text-lg tracking-tight">
            {isES ? 'Solicitudes de campaña de clientes' : 'Client campaign requests'}
          </h3>
          {requests.length > 0 && <Badge className="bg-amber-100 text-amber-700 text-xs">{requests.length}</Badge>}
        </div>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="climara-requests-empty">
            {isES ? 'No hay solicitudes pendientes.' : 'No pending requests.'}
          </p>
        ) : requests.map((r) => (
          <div key={r.id} className="border rounded-lg p-4 mb-3 last:mb-0" data-testid={`climara-request-${r.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm font-semibold">“{r.name}” · {r.wave_label}</p>
                <p className="text-xs text-muted-foreground">
                  {r.company_name} · {r.invited_count} {isES ? 'invitados' : 'invited'} ·
                  {' '}{isES ? 'solicitado por' : 'requested by'} {r.requested_by_name}
                  {r.deadline && <> · {isES ? 'límite' : 'due'} {r.deadline}</>}
                </p>
                {r.notes && <p className="text-xs text-muted-foreground mt-1 italic">“{r.notes}”</p>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={qSel[r.id] || ''} onValueChange={(v) => setQSel((p) => ({ ...p, [r.id]: v }))}>
                  <SelectTrigger className="w-56 h-8 text-xs" data-testid={`climara-request-q-${r.id}`}>
                    <SelectValue placeholder={isES ? 'Cuestionario certificado…' : 'Certified questionnaire…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {questionnaires.map((q) => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => approve(r.id)} disabled={busy === r.id}
                        data-testid={`climara-request-approve-${r.id}`}>
                  {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                  {isES ? 'Aprobar y lanzar' : 'Approve & launch'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRejecting(rejecting === r.id ? null : r.id)}
                        data-testid={`climara-request-reject-${r.id}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1" />{isES ? 'Rechazar' : 'Reject'}
                </Button>
              </div>
            </div>
            {rejecting === r.id && (
              <div className="mt-2 flex gap-2">
                <Textarea rows={1} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                          placeholder={isES ? 'Motivo (visible para el cliente)…' : 'Reason (visible to the client)…'}
                          data-testid={`climara-request-reject-reason-${r.id}`} />
                <Button size="sm" variant="destructive" onClick={() => reject(r.id)} disabled={busy === r.id}
                        data-testid={`climara-request-reject-confirm-${r.id}`}>
                  {isES ? 'Confirmar' : 'Confirm'}
                </Button>
              </div>
            )}
          </div>
        ))}
      </Card>
      )}

      {/* Team building manager */}
      {section !== 'requests' && (
      <Card className="p-6 shadow-card" data-testid="climara-teambuilding-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h3 className="font-display font-bold text-lg tracking-tight">Team Building</h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={reportSel} onValueChange={setReportSel}>
              <SelectTrigger className="w-64 h-8 text-xs" data-testid="climara-tb-report-select">
                <SelectValue placeholder={isES ? 'Informe publicado…' : 'Released report…'} />
              </SelectTrigger>
              <SelectContent>
                {reports.filter((x) => x.status === 'released').map((x) => (
                  <SelectItem key={x.id} value={x.id}>
                    {(x.campaign_name || x.payload?.campaign?.name || (isES ? 'Informe CLIMARA' : 'CLIMARA report'))}
                    {x.wave_label ? ` · ${x.wave_label}` : ` · ${bizDate(x.released_at, locale)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={generate} disabled={busy === 'gen'} data-testid="climara-tb-generate">
              {busy === 'gen' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
              {isES ? 'Generar recomendaciones IA' : 'Generate AI recommendations'}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {isES
            ? 'Flujo asesor: la IA sugiere actividades a partir de la medición → tú las revisas → propones al cliente → el cliente decide. Nunca se ejecuta nada automáticamente.'
            : 'Advisory flow: AI suggests activities from the wave → you curate → propose to the client → the client decides. Nothing executes automatically.'}
        </p>
        {proposals.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="climara-tb-admin-empty">
            {isES ? 'Sin propuestas todavía. Genera recomendaciones desde un informe publicado.' : 'No proposals yet. Generate recommendations from a released report.'}
          </p>
        ) : proposals.map((p) => (
          <div key={p.id} className="border rounded-lg p-4 mb-3 last:mb-0" data-testid={`climara-tb-admin-${p.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <p className="text-xs text-muted-foreground">
                {bizDate(p.created_at, locale)} · {p.generated_by === 'ai' ? (isES ? 'Generada por IA' : 'AI generated') : (isES ? 'Metodología CTH' : 'CTH methodology')}
                {p.decided_by_name && <> · {isES ? 'decidido por' : 'decided by'} {p.decided_by_name}</>}
              </p>
              <div className="flex items-center gap-2">
                {statusBadge(p.status)}
                <Button size="sm" variant="outline" onClick={() => downloadTbPdf(p.id)}
                        data-testid={`climara-tb-admin-pdf-${p.id}`}>
                  <Download className="h-3.5 w-3.5 mr-1" />PDF
                </Button>
                {p.followup_status && (
                  <Badge variant="outline" className="text-xs" data-testid={`climara-tb-fustate-${p.id}`}>
                    {TB_STATE_LABEL(p.followup_status, isES)}
                  </Badge>
                )}
                {p.status === 'draft' && (
                  <Button size="sm" onClick={() => propose(p.id)} disabled={busy === p.id}
                          data-testid={`climara-tb-propose-${p.id}`}>
                    {busy === p.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                    {isES ? 'Proponer al cliente' : 'Propose to client'}
                  </Button>
                )}
                {['accepted', 'declined'].includes(p.status) && (
                  <Button size="sm" variant="outline" onClick={() => setFuOpen(fuOpen === p.id ? null : p.id)}
                          data-testid={`climara-tb-followup-btn-${p.id}`}>
                    <ClipboardList className="h-3.5 w-3.5 mr-1" />
                    {isES ? 'Registrar seguimiento' : 'Register follow-up'}
                  </Button>
                )}
                {(p.history || []).length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setHistOpen(histOpen === p.id ? null : p.id)}
                          data-testid={`climara-tb-history-btn-${p.id}`}>
                    <History className="h-3.5 w-3.5 mr-1" />
                    {isES ? 'Historial' : 'History'}
                  </Button>
                )}
              </div>
            </div>
            {p.status === 'accepted' && !(p.followups || []).length && (
              <div className="rounded-md border border-amber-300 bg-amber-50/70 px-3 py-2 mb-2 text-xs text-amber-900"
                   data-testid={`climara-tb-followup-pending-${p.id}`}>
                {isES
                  ? 'Propuesta de Team Building aceptada — seguimiento requerido. Registra el próximo paso para que la oportunidad no muera en silencio.'
                  : 'Team Building proposal accepted — follow-up required. Register the next step so the opportunity does not die silently.'}
              </div>
            )}
            {fuOpen === p.id && (
              <div className="rounded-md border bg-muted/20 p-3 mb-2 space-y-2" data-testid={`climara-tb-followup-form-${p.id}`}>
                <Textarea rows={2} value={fu.action_text}
                          onChange={(e) => setFu((s) => ({ ...s, action_text: e.target.value }))}
                          placeholder={isES ? 'Acción realizada / próximo paso…' : 'Action taken / next step…'}
                          data-testid={`climara-tb-fu-action-${p.id}`} />
                <div className="flex flex-wrap items-center gap-2">
                  <Input type="date" value={fu.follow_up_date}
                         onChange={(e) => setFu((s) => ({ ...s, follow_up_date: e.target.value }))}
                         className="h-8 w-40 text-xs" data-testid={`climara-tb-fu-date-${p.id}`} />
                  <Select value={fu.state} onValueChange={(v) => setFu((s) => ({ ...s, state: v }))}>
                    <SelectTrigger className="w-52 h-8 text-xs" data-testid={`climara-tb-fu-state-${p.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TB_FOLLOWUP_STATES.map(([v, es, en]) => (
                        <SelectItem key={v} value={v}>{isES ? es : en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={fu.note} onChange={(e) => setFu((s) => ({ ...s, note: e.target.value }))}
                         placeholder={isES ? 'Nota (opcional)' : 'Note (optional)'}
                         className="h-8 flex-1 min-w-[10rem] text-xs" data-testid={`climara-tb-fu-note-${p.id}`} />
                  <Button size="sm" onClick={() => registerFollowUp(p.id)} disabled={busy === `fu-${p.id}`}
                          data-testid={`climara-tb-fu-save-${p.id}`}>
                    {busy === `fu-${p.id}` && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    {isES ? 'Guardar seguimiento' : 'Save follow-up'}
                  </Button>
                </div>
              </div>
            )}
            {histOpen === p.id && (
              <div className="rounded-md border bg-muted/10 p-3 mb-2" data-testid={`climara-tb-history-${p.id}`}>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                  {isES ? 'Historial' : 'History'}
                </p>
                <ul className="space-y-1 text-xs">
                  {(p.history || []).slice().reverse().map((h, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-muted-foreground shrink-0 tabular-nums">{bizDateTime(h.at, locale)}</span>
                      <span>{(isES ? h.label_es : h.label_en) || h.label_es || h.type}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid md:grid-cols-3 gap-2">
              {(p.activities || []).map((a, i) => (
                <div key={i} className="rounded-md border bg-muted/30 p-2.5">
                  <p className="text-xs font-semibold">{a.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{a.objective}</p>
                  <p className="text-xs text-muted-foreground mt-1">{a.audience} · {a.duration} · {a.frequency}</p>
                </div>
              ))}
            </div>
            {p.client_note && (
              <p className="text-xs text-muted-foreground mt-2">{isES ? 'Comentario del cliente:' : 'Client note:'} “{p.client_note}”</p>
            )}
          </div>
        ))}
      </Card>
      )}
    </>
  );
}
